import { sumArray } from "@/utils/helpers/common";
import { BookingStatus } from "@/types/db/product-slot-state";
import { BookingTeamsStatus } from "@/interfaces/teams/teams";
import type { IBookingTicket } from "@/interfaces/store/IBooking";

type Row = Record<string, unknown>;

type RetailRow = { id: number; price_name: string; quantity: number };

type RawProduct = { id?: number | string };
type RawTicket = {
  id?: number;
  name?: string;
  product_name?: string;
  product_id?: number | string;
  booking_status?: number;
  price_id?: number | string;
};
type RawTeam = {
  team_cancelled?: boolean;
  team_room_players?: number | string;
};
type RawTeamSetting = { team_setting_counted_psp?: Array<number | string> };

export type ParsedRow = {
  tickets: IBookingTicket[];
  retails: RetailRow[];
  requireTeams: boolean;
  teamsStatus: BookingTeamsStatus;
};

const cache = new WeakMap<Row, ParsedRow>();

/**
 * Parse a single elasticsearch booking row into the per-cell-display shapes
 * (tickets, retails, teams status) once, cached by row reference.
 *
 * Cells used to do this work themselves — three separate cells were each
 * doing `JSON.parse(row.bookingTickets[i])` and `JSON.parse(row.bookingProducts[i])`,
 * with two of them additionally re-deriving the retail-vs-ticket partition.
 * Centralising here removes the duplication and lets the cells stay "dumb".
 *
 * Cache uses a WeakMap keyed by the row object reference. SSearchTable hands
 * cells a stable row object per booking id (within a fetch cycle), so cache
 * hits are common. When SSearchTable re-fetches and rows change identity,
 * stale entries become unreachable and are GC'd.
 *
 * @param {Row} row - Raw booking row from SSearchTable.
 * @returns {ParsedRow} Parsed view-model used by Tickets / Retails / Teams cells.
 */
export function useParsedRow(row: Row): ParsedRow {
  const cached = cache.get(row);

  if (cached) {
    return cached;
  }

  const parsed = parseRow(row);

  cache.set(row, parsed);

  return parsed;
}

/**
 * Internal — parse a row from scratch. Mirrors the original `parsedOrders`
 * computed in pre-migration Orders.vue.
 *
 * @param {Row} row - Raw booking row.
 * @returns {ParsedRow} Parsed view-model.
 */
function parseRow(row: Row): ParsedRow {
  const rawTickets = (row.bookingTickets as string[] | undefined) ?? [];
  const rawProducts = (row.bookingProducts as string[] | undefined) ?? [];
  const rawQuantity =
    (row.bookingsTicketQuantity as string[] | undefined) ?? [];
  const rawTeams = (row.bookingTeam as string[] | undefined) ?? [];
  const rawTeamSettings =
    (row.bookingTeamSetting as string[] | undefined) ?? [];

  // -- Retail product ids (filter source for tickets and retails) --
  const retailProductIds = new Set<number>();

  for (const productJson of rawProducts) {
    if (!productJson.includes("Retail")) {
      continue;
    }

    try {
      const parsed = JSON.parse(productJson) as RawProduct;

      retailProductIds.add(Number(parsed.id));
    } catch (err) {
      console.warn("[useParsedRow] bad bookingProducts entry", err);
    }
  }

  // -- Tickets: dedupe by id, drop entries whose product_id is in retails --
  const tickets: IBookingTicket[] = [];
  const seenTicketIds = new Set<number>();

  for (const json of rawTickets) {
    let parsed: RawTicket | null = null;

    try {
      parsed = JSON.parse(json) as RawTicket;
    } catch (err) {
      console.warn("[useParsedRow] bad bookingTickets entry", err);
      continue;
    }

    if (!parsed || parsed.id === undefined) {
      continue;
    }

    if (seenTicketIds.has(parsed.id)) {
      continue;
    }

    seenTicketIds.add(parsed.id);

    if (retailProductIds.has(Number(parsed.product_id))) {
      continue;
    }

    tickets.push({
      id: parsed.id,
      name: parsed.name ?? "",
      productName: parsed.product_name ?? "",
    });
  }

  // -- Retails: bookingsTicketQuantity entries whose id is NOT a surviving ticket --
  const surfaceTicketIds = new Set(tickets.map((t) => t.id));
  const retails: RetailRow[] = [];

  for (const json of rawQuantity) {
    try {
      const parsed = JSON.parse(json) as RetailRow;

      if (!surfaceTicketIds.has(parsed.id)) {
        retails.push(parsed);
      }
    } catch (err) {
      console.warn("[useParsedRow] bad bookingsTicketQuantity entry", err);
    }
  }

  // -- Teams: status calculation matching pre-migration parsedOrders --
  const requireTeams = rawTeamSettings.length > 0;
  const teamsStatus = computeTeamsStatus(
    requireTeams,
    rawTeams,
    rawTickets,
    rawTeamSettings
  );

  return { tickets, retails, requireTeams, teamsStatus };
}

/**
 * Compute teams status by replaying the parser used in the original
 * Orders.vue `parsedOrders` computed: count active team players, count
 * tickets whose price_id is in team_setting_counted_psp, compare.
 *
 * @param {boolean} requireTeams - True when bookingTeamSetting is non-empty.
 * @param {string[]} rawTeams - Raw json-string entries of row.bookingTeam.
 * @param {string[]} rawTickets - Raw json-string entries of row.bookingTickets.
 * @param {string[]} rawTeamSettings - Raw json-string entries of row.bookingTeamSetting.
 * @returns {BookingTeamsStatus} Teams status enum.
 */
function computeTeamsStatus(
  requireTeams: boolean,
  rawTeams: string[],
  rawTickets: string[],
  rawTeamSettings: string[]
): BookingTeamsStatus {
  if (!requireTeams) {
    return BookingTeamsStatus.NotStarted;
  }

  try {
    const teams: RawTeam[] = rawTeams
      .map((s) => JSON.parse(s) as RawTeam)
      .filter((t) => !t.team_cancelled && t.team_room_players);

    if (!teams.length) {
      return BookingTeamsStatus.NotStarted;
    }

    const players = sumArray(teams.map((t) => Number(t.team_room_players)));

    const countedPsp: number[] = rawTeamSettings
      .map(
        (s) => (JSON.parse(s) as RawTeamSetting).team_setting_counted_psp ?? []
      )
      .flat()
      .map((id) => Number(id));

    const tickets: RawTicket[] = rawTickets
      .map((s) => JSON.parse(s) as RawTicket)
      .filter(
        (t) =>
          t.booking_status !== BookingStatus.Canceled &&
          t.booking_status !== BookingStatus.Abandoned &&
          countedPsp.includes(Number(t.price_id))
      );

    if (tickets.length > players) {
      return BookingTeamsStatus.InProgress;
    }

    return BookingTeamsStatus.Completed;
  } catch (err) {
    console.error("[useParsedRow] teams status compute failed", err);

    return BookingTeamsStatus.NotStarted;
  }
}
